import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import SEOHead from '../../components/SEOHead';
import { generateBreadcrumbStructuredData } from '../../utils/seoUtils';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';



const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Privacy Policy', path: '/privacy-policy' }
  ];

  const breadcrumbData = generateBreadcrumbStructuredData(breadcrumbs);

  return (
    <>
      <SEOHead
        pageType="privacy"
        title="Privacy Policy | ParkTrip AI"
        description="Learn how ParkTrip Planner AI protects your privacy and handles your personal data."
        canonicalUrl="https://parktripplannerai.com/privacy-policy"
        structuredData={breadcrumbData}
        keywords="privacy policy, data protection, ParkTrip AI"
        ogImage="https://parktripplannerai.com/og-privacy.jpg"
      />

      <div className="min-h-screen bg-background flex flex-col">
        
        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm mb-8">
              <Link to="/" className="text-muted hover:text-foreground transition-organic flex items-center">
                <Icon name="Home" size={16} className="mr-1" />
                Home
              </Link>
              <Icon name="ChevronRight" size={16} className="text-muted" />
              <span className="text-foreground font-medium">Privacy Policy</span>
            </div>

            {/* Header */}
            <div className="mb-8">
              <h1 className="font-heading font-heading-bold text-4xl text-foreground mb-2">
                Privacy Policy
              </h1>
              <p className="font-body text-muted">Last Updated: November 2025</p>
            </div>

            {/* Content */}
            <div className="bg-card rounded-organic-lg p-8 shadow-sm">
              <div className="prose prose-slate max-w-none">
                <p className="font-body text-base text-muted-foreground leading-relaxed">
                  Your privacy matters to us. This Privacy Policy explains how ParkTrip Planner AI ("we," "our") collects, uses, and protects your information when you use our Service.
                </p>

                {/* Section 1 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">1. Information We Collect</h2>
                  <p className="font-body text-foreground mb-4">We may collect the following:</p>
                  
                  <div className="mb-4">
                    <h3 className="font-heading font-heading-semibold text-xl text-foreground mb-3">a. Information You Provide</h3>
                    <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                      <li>Email address</li>
                      <li>Name (optional)</li>
                      <li>Park preferences</li>
                      <li>Saved trips and itineraries</li>
                      <li>Feedback or support requests</li>
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h3 className="font-heading font-heading-semibold text-xl text-foreground mb-3">b. Automatically Collected Information</h3>
                    <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                      <li>Device type</li>
                      <li>Browser type</li>
                      <li>IP address</li>
                      <li>Usage data (pages visited, actions taken)</li>
                      <li>Cookies and analytics data</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-heading font-heading-semibold text-xl text-foreground mb-3">c. Newsletter Signups</h3>
                    <p className="font-body text-foreground mb-2">
                      If you subscribe to our newsletter, we collect your email and opt-in status.
                    </p>
                    <p className="font-body text-foreground">
                      We do not collect payment information (no payments are currently processed).
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">2. How We Use Your Information</h2>
                  <p className="font-body text-foreground mb-3">We use your data to:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Generate AI-based trip itineraries</li>
                    <li>Improve site performance and features</li>
                    <li>Provide customer support</li>
                    <li>Track analytics (user activity, retention, engagement)</li>
                    <li>Send newsletters or updates (if you opt in)</li>
                    <li>Prevent fraud or security issues</li>
                  </ul>
                  <p className="font-body text-foreground mt-3 font-semibold">
                    We never sell your personal information.
                  </p>
                </section>

                {/* Section 3 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">3. Cookies & Tracking</h2>
                  <p className="font-body text-foreground mb-3">We may use cookies to:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Save your preferences</li>
                    <li>Keep you signed in</li>
                    <li>Understand usage patterns</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    You can disable cookies in your browser settings, but some features may not function properly.
                  </p>
                </section>

                {/* Section 4 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">4. Sharing Your Information</h2>
                  <p className="font-body text-foreground mb-3">We may share your information only with:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Analytics providers (e.g., Google Analytics)</li>
                    <li>Email or newsletter services (e.g., Mailchimp)</li>
                    <li>Infrastructure providers (hosting, storage)</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    These partners are required to safeguard your data.
                  </p>
                  <p className="font-body text-foreground font-semibold mt-2">
                    We do not share your information with advertisers or data brokers.
                  </p>
                </section>

                {/* Section 5 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">5. Data Retention</h2>
                  <p className="font-body text-foreground">
                    We retain data only as long as necessary to provide the Service, or until you request deletion.
                  </p>
                </section>

                {/* Section 6 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">6. Data Security</h2>
                  <p className="font-body text-foreground">
                    We use industry-standard security practices to protect your information. No system is 100% secure, but we work to safeguard your data from unauthorized access.
                  </p>
                </section>

                {/* Section 7 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">7. Your Rights</h2>
                  <p className="font-body text-foreground mb-3">Depending on your jurisdiction, you may have rights to:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Access your data</li>
                    <li>Request deletion</li>
                    <li>Update inaccurate information</li>
                    <li>Opt out of newsletters</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    You can contact us anytime for assistance.
                  </p>
                </section>

                {/* Section 8 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">8. Children's Privacy</h2>
                  <p className="font-body text-foreground">
                    Our Service is not directed to children under 13. We do not knowingly collect information from children.
                  </p>
                </section>

                {/* Section 9 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">9. Changes to This Policy</h2>
                  <p className="font-body text-foreground">
                    We may update this Privacy Policy from time to time. Continued use of the Service means you accept the updated Policy.
                  </p>
                </section>

                {/* Section 10 */}
                <section>
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">10. Contact Us</h2>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    For privacy inquiries, contact us at{' '}
                    <a href="mailto:privacy@parktripplannerai.com" className="text-primary hover:text-primary/80 transition-organic">
                      privacy@parktripplannerai.com
                    </a>
                  </p>
                </section>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-organic font-medium"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Home</span>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;